import { cva, type VariantProps } from 'class-variance-authority';

import type { JSX, MouseEventHandler } from 'react';

import { cn, type TIcon } from '~/utils';
import { Show } from '~/components/show';
import { XIcon } from '~/components/icons';
import { Badge, type IBadgeProps } from '~/components/badge';

export const chipVariants = cva(
  cn(
    '[&>svg]:size-3.5 rounded-[75px] bg-blueBonnet-50 text-blueBonnet-500 text-sm gap-1.5 cursor-default items-center relative',
    'hover:border-blueBonnet-500',
    '[--svg-color:var(--color-blueBonnet-500)]',
    'data-[selected=true]:border-blueBonnet-500',
  ),
  {
    variants: {
      size: {
        default: 'px-2.5 py-[6.5px]',
        sm: 'px-2.5 py-[5px]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

export interface IChipOptionProps {
  icon?: TIcon;
  value: string;
  label: string;
  removeable?: boolean;
}

export interface IChipProps
  extends Omit<IBadgeProps, 'onClick'>,
    IChipOptionProps,
    VariantProps<typeof chipVariants> {
  selected?: boolean;
  onClick?: (value: string) => void;
  onRemove?: (value: string) => void;
}

export function Chip({
  icon,
  size,
  label,
  value,
  onClick,
  onRemove,
  className,
  selected = false,
  removeable = true,
  ...props
}: IChipProps): JSX.Element {
  const Icon = icon as TIcon;

  const handleRemove: MouseEventHandler<HTMLSpanElement> = (e) => {
    e.preventDefault();

    onRemove?.(value);
  };

  const handleClick = () => {
    if (onClick) {
      onClick(value);
    }
  };

  return (
    <Badge
      data-selected={selected}
      className={cn(
        chipVariants({
          size,
        }),
        className,
      )}
      {...props}
    >
      <Show when={onClick}>
        <div
          role='button'
          onClick={handleClick}
          className='absolute inset-0 cursor-pointer'
        />
      </Show>
      <Show when={icon}>
        <Icon />
      </Show>
      {label}
      <Show when={removeable}>
        <span
          role='button'
          onClick={handleRemove}
          className='cursor-pointer relative z-50'
        >
          <XIcon className='size-3.5' />
        </span>
      </Show>
    </Badge>
  );
}
